import { Component } from "@theme/component";
import { clamp, prefersReducedMotion } from "@theme/utilities";

/**
 * Slides the footer up over the pinned page content — the same motion as the stacked
 * sliders panels, with the page content playing the pinned panel and the footer the
 * one sliding over it.
 *
 * The overlap itself is mostly CSS: the page content is pinned with `position: sticky`
 * so the footer slides up over it without the scroll ever being hijacked. This
 * component adds the parts CSS can't express: the pin offset (which depends on the
 * content height, only measurable at runtime), how far the content has been covered
 * (`--stacked-progress`, which drives the optional scale/dim), and the snap behavior
 * copied from the stacked sliders — including `smooth` mode, which takes the gesture
 * over and glides the footer fully into view.
 *
 * Rendered inside the footer section when its "Swipe up over page content" setting is
 * on; the two snap markers it may contain mirror the stacked sliders' markers.
 */
/**
 * Whether the document scroll is locked by an open drawer or dialog. Gestures on those
 * must not be taken over: paging scrolls with `scrollTo`, which `overflow: hidden`
 * cannot block, so the page would move behind the overlay.
 *
 * @returns {boolean} True while a scroll-locking overlay is open.
 */
function scrollLocked() {
  return (
    document.documentElement.hasAttribute("scroll-lock") ||
    !!document.querySelector("details[scroll-lock][open], dialog[open]")
  );
}

/** Wheel events further apart than this, in milliseconds, belong to different gestures. */
const WHEEL_GESTURE_GAP = 200;

/** Wheel travel a gesture has to accumulate, in pixels, before it pages. */
const WHEEL_PAGE_THRESHOLD = 50;

/** Weight of the latest wheel event in the running average of the gesture's travel. */
const WHEEL_SMOOTHING = 0.3;

/** Wheel travel below this, in pixels, is too small to say anything about the gesture. */
const WHEEL_NOISE_FLOOR = 8;

/** How long the scroll has to be still, in milliseconds, before the reveal settles. */
const SETTLE_DELAY = 150;

/**
 * The vertical travel of a wheel event, in pixels. Browsers may report it in lines
 * or pages instead — Firefox does for a mouse wheel — and those would never add up
 * to a pixel threshold.
 *
 * @param {WheelEvent} event - The wheel event.
 * @returns {number} The vertical delta, in pixels.
 */
function wheelDeltaY(event) {
  switch (event.deltaMode) {
    case WheelEvent.DOM_DELTA_LINE:
      return event.deltaY * 16;
    case WheelEvent.DOM_DELTA_PAGE:
      return event.deltaY * window.innerHeight;
    default:
      return event.deltaY;
  }
}

class FooterReveal extends Component {
  requiredRefs = [];

  /** @type {AbortController | undefined} */
  #abortController;
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;
  /** @type {number | undefined} */
  #frame;
  /** @type {HTMLElement | null} The pinned page content. */
  #main = null;
  /** @type {HTMLElement | null} The footer's section wrapper, i.e. the sliding panel. */
  #wrapper = null;
  /** Scroll position at which the footer starts entering the viewport. */
  #start = 0;
  /** Scroll distance of the transition: the footer's height. */
  #step = 0;
  /** @type {'proximity' | 'mandatory' | null} */
  #snapMode = null;
  /** Snapping is released while a programmatic scroll is in flight. */
  #suppressSnap = false;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #releaseTimer;
  /** @type {number | undefined} Frame of the reveal animation in `smooth` mode. */
  #pageFrame;
  /** A move asked for while another was still animating, played once it lands: 1, -1 or 0. */
  #pendingDirection = 0;
  /** @type {ReturnType<typeof setTimeout> | undefined} Waits for a scroll to come to rest. */
  #settleTimer;
  /** @type {number | null} Where the current touch gesture started. */
  #touchStart = null;
  /** Whether the current touch gesture has already paged: one swipe, one transition. */
  #touchSpent = false;
  /** Accumulated travel of the current wheel gesture, in pixels. */
  #wheelDelta = 0;
  /** Timestamp of the last wheel event. */
  #wheelLast = 0;
  /** Direction of the current wheel gesture: 1 revealing, -1 closing. */
  #wheelDirection = 0;
  /** Running average of the current wheel gesture's travel per event, in pixels. */
  #wheelAverage = 0;
  /** The highest the running average has been during the current wheel gesture. */
  #wheelPeak = 0;
  /** The lowest the running average has been since the gesture started coasting. */
  #wheelTrough = 0;
  /** Whether the current wheel gesture has tailed off into trackpad momentum. */
  #wheelCoasting = false;
  /** Whether the current wheel gesture has already paged: one flick, one transition. */
  #wheelSpent = false;

  connectedCallback() {
    super.connectedCallback();

    this.#main = document.getElementById("MainContent");
    this.#wrapper = this.closest(".shopify-section");

    if (!this.#main || !this.#wrapper) return;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    window.addEventListener("scroll", this.#requestUpdate, {
      passive: true,
      signal,
    });
    // Scrolls the reveal doesn't drive itself still have to end open or closed.
    window.addEventListener("scroll", this.#scheduleSettle, {
      passive: true,
      signal,
    });
    window.addEventListener("resize", this.#handleResize, {
      passive: true,
      signal,
    });

    // `smooth` mode takes the gesture over and animates the reveal, so these have to be
    // cancellable.
    window.addEventListener("wheel", this.#handleWheel, {
      passive: false,
      signal,
    });
    window.addEventListener("touchstart", this.#handleTouchStart, {
      passive: true,
      signal,
    });
    window.addEventListener("touchmove", this.#handleTouchMove, {
      passive: false,
      signal,
    });
    window.addEventListener("keydown", this.#handleKeydown, { signal });

    // The pin offset tracks the content height, and the transition length tracks the
    // footer height.
    this.#resizeObserver = new ResizeObserver(this.#handleResize);
    this.#resizeObserver.observe(this.#main);
    this.#resizeObserver.observe(this.#wrapper);

    this.#measure();
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
    this.#resizeObserver?.disconnect();

    if (this.#frame) cancelAnimationFrame(this.#frame);
    if (this.#pageFrame) cancelAnimationFrame(this.#pageFrame);
    clearTimeout(this.#releaseTimer);
    clearTimeout(this.#settleTimer);

    this.#snapMode = null;
    delete document.documentElement.dataset.footerSnap;

    // Unpin the content, e.g. when the setting is turned off in the theme editor.
    if (this.#main) {
      this.#main.style.position = "";
      this.#main.style.top = "";
      this.#main.style.removeProperty("--stacked-progress");
    }
  }

  updatedCallback() {
    super.updatedCallback();

    this.#measure();
    this.#update();
  }

  /**
   * Pins the content and measures the transition. None of this changes while scrolling,
   * only when the viewport, the content or the footer change.
   */
  #measure() {
    const main = this.#main;
    const wrapper = this.#wrapper;

    if (!main || !wrapper) return;

    // Pin the content so its end freezes in place while the footer slides over it. The
    // offset displaces the sticky box downward by exactly the footer's height — the
    // room the document gains from the footer itself. `dvh` rather than `svh`: on
    // mobile the browser chrome retracts on scroll, and an `svh` offset would leave
    // the content's end floating above the real viewport bottom with a gap under it.
    const top = `calc(100dvh - ${main.offsetHeight}px)`;

    if (main.style.position !== "sticky") main.style.position = "sticky";
    if (main.style.top !== top) main.style.top = top;

    this.#step = wrapper.offsetHeight;

    this.#measureBounds();
  }

  /**
   * Locates the transition in the document. Re-read on every update because anything
   * above the footer — images loading, fonts swapping, a section re-rendering — moves it.
   */
  #measureBounds() {
    if (!this.#wrapper) return;

    const wrapperTop =
      this.#wrapper.getBoundingClientRect().top + window.scrollY;

    this.#start = wrapperTop - window.innerHeight;
  }

  /** @returns {number} The transition progress: 0 footer closed, 1 fully revealed. */
  #position() {
    return this.#step ? (window.scrollY - this.#start) / this.#step : 0;
  }

  #requestUpdate = () => {
    if (this.#frame) return;

    this.#frame = requestAnimationFrame(() => {
      this.#frame = undefined;
      this.#update();
    });
  };

  #handleResize = () => {
    this.#measure();
    this.#update();
  };

  #update() {
    if (!this.#step) return;

    // Read layout first, write after: everything below only sets custom properties and
    // attributes.
    this.#measureBounds();

    const position = this.#position();

    // Progress is only worth writing when an effect actually reads it — setting a custom
    // property on the content invalidates the styles of everything inside it.
    if (this.hasAttribute("data-effects") && this.#main) {
      const progress = clamp(position, 0, 1);

      this.#main.style.setProperty("--stacked-progress", progress.toFixed(4));
    }

    this.#syncSnapRequest(position);
  }

  /**
   * Claims document scroll snapping while the transition is near.
   *
   * `mandatory` snapping is only ever held *inside* the transition: the document
   * scroller cannot be scrolled past a mandatory snap position, so holding it at the
   * edges would trap the visitor against the footer.
   *
   * @param {number} position - The transition progress.
   */
  #syncSnapRequest(position) {
    const setting = this.dataset.snap;
    /** @type {'proximity' | 'mandatory' | null} */
    let mode = null;

    // `smooth` mode moves the scroll itself, so CSS snapping would only fight it.
    const snaps = setting === "gentle" || setting === "strict";

    // Engaged from one viewport away, released again once the visitor scrolls back up
    // the page, so the rest of the page scrolls normally.
    if (snaps && !this.#suppressSnap && position > -0.98 && position <= 1.02) {
      const inside = position > 0.01 && position < 0.99;

      mode = setting === "strict" && inside ? "mandatory" : "proximity";
    }

    if (mode === this.#snapMode) return;

    this.#snapMode = mode;

    if (mode) {
      document.documentElement.dataset.footerSnap = mode;
    } else {
      delete document.documentElement.dataset.footerSnap;
    }
  }

  /**
   * Drops the snapping claim until the current scroll comes to a stop. `scrollend`
   * isn't available everywhere yet, so a timer backs it up.
   */
  #releaseSnap() {
    this.#suppressSnap = true;
    this.#snapMode = null;
    delete document.documentElement.dataset.footerSnap;

    const restore = () => {
      window.removeEventListener("scrollend", restore);
      clearTimeout(this.#releaseTimer);

      this.#suppressSnap = false;
      this.#requestUpdate();
    };

    clearTimeout(this.#releaseTimer);
    window.addEventListener("scrollend", restore);
    this.#releaseTimer = setTimeout(restore, 1500);
  }

  /**
   * Whether gestures should be taken over and animated. Only in `smooth` mode, and only
   * once the footer is at the edge of (or inside) the viewport — further up the page the
   * scroll is left alone.
   *
   * @returns {boolean} True when gestures should be paged.
   */
  #paging() {
    return (
      this.dataset.snap === "smooth" &&
      this.#step > 0 &&
      this.#position() > -0.02 &&
      !scrollLocked()
    );
  }

  /**
   * Whether there is anywhere to go in the given direction. At either end the gesture is
   * left alone so the visitor can carry on up the page.
   *
   * @param {number} direction - 1 when revealing the footer, -1 when closing it.
   * @returns {boolean} True when the transition can move that way.
   */
  #canPage(direction) {
    const position = this.#position();

    return direction > 0 ? position < 0.98 : position > 0.02;
  }

  /**
   * Glides the footer fully open or closed.
   *
   * A move asked for while the previous one is still animating is kept and played
   * once that one lands, so a quick flick back isn't lost.
   *
   * @param {number} direction - 1 when revealing the footer, -1 when closing it.
   */
  #page(direction) {
    if (this.#pageFrame) {
      this.#pendingDirection = direction;
      return;
    }

    this.#pendingDirection = 0;
    this.#releaseSnap();
    this.#animateTo(this.#start + (direction > 0 ? 1 : 0) * this.#step);
  }

  /** Plays the move queued while the last animation ran, if there is still somewhere to go. */
  #flushPending() {
    const direction = this.#pendingDirection;

    this.#pendingDirection = 0;

    if (direction && this.#paging() && this.#canPage(direction)) {
      this.#page(direction);
    }
  }

  /**
   * Scrolls to a position over a fixed, snappy duration. `scroll-behavior: smooth` is
   * duration-less and eases differently per distance, so the animation is driven here
   * instead.
   *
   * @param {number} top - The scroll position to land on.
   */
  #animateTo(top) {
    const from = window.scrollY;
    const distance = top - from;

    if (!distance) return;

    // A hidden tab gets no animation frames, so an animation started there would never
    // finish and would swallow every gesture after it.
    if (prefersReducedMotion() || document.hidden) {
      window.scrollTo({ top, behavior: "instant" });
      return;
    }

    const duration = Number(this.dataset.snapSpeed) || 600;
    const started = performance.now();

    /** @param {number} now - The current frame timestamp. */
    const tick = (now) => {
      // Frames stop coming if the tab is hidden part way through: land on the target
      // instead of leaving the page parked mid-reveal.
      if (document.hidden) {
        window.scrollTo({ top, behavior: "instant" });
        this.#pageFrame = undefined;
        this.#pendingDirection = 0;
        return;
      }

      const elapsed = clamp((now - started) / duration, 0, 1);
      // easeOutCubic: leaves immediately at full pace, settles without a bounce.
      const eased = 1 - Math.pow(1 - elapsed, 3);

      window.scrollTo({ top: from + distance * eased, behavior: "instant" });

      if (elapsed < 1) {
        this.#pageFrame = requestAnimationFrame(tick);
        return;
      }

      this.#pageFrame = undefined;
      this.#flushPending();
    };

    this.#pageFrame = requestAnimationFrame(tick);
  }

  /**
   * Lands the reveal fully open or closed once a scroll the component didn't drive
   * has come to rest — a fling Chromium kept for itself, a scrollbar drag, a smooth
   * scroll cut short — so the footer is never left parked half revealed.
   */
  #scheduleSettle = () => {
    clearTimeout(this.#settleTimer);
    this.#settleTimer = setTimeout(this.#settle, SETTLE_DELAY);
  };

  #settle = () => {
    if (this.#pageFrame || !this.#paging()) return;

    this.#measureBounds();

    const position = this.#position();

    // Only mid-transition: closed or fully revealed, there is nothing to settle.
    if (position <= 0 || position >= 1) return;

    const target = this.#start + Math.round(position) * this.#step;

    if (Math.abs(target - window.scrollY) <= 1) return;

    this.#releaseSnap();
    this.#animateTo(target);
  };

  /** @param {WheelEvent} event - The wheel event. */
  #handleWheel = (event) => {
    if (event.ctrlKey || !this.#paging()) return;

    // Ties go to vertical: the first event of a trackpad swipe is tiny and often
    // diagonal, and once a gesture's first event goes uncancelled Chromium hands the
    // rest of it to native scrolling.
    if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;

    const delta = wheelDeltaY(event);

    if (!delta) return;

    const direction = delta > 0 ? 1 : -1;

    if (!this.#canPage(direction)) return;

    // A gesture Chromium has already handed to native scrolling can't be taken over:
    // animating against its fling would park the footer half revealed. Let it run — the
    // scroll settles once it stops.
    if (!event.cancelable) return;

    // Swallow the gesture even while an animation is running, otherwise the page jumps
    // ahead of it.
    event.preventDefault();

    this.#trackWheel(direction, Math.abs(delta));

    // Accumulating up to a threshold keeps a stray notch or a light trackpad touch from
    // triggering the transition.
    if (this.#wheelSpent || this.#wheelDelta < WHEEL_PAGE_THRESHOLD) return;

    this.#wheelSpent = true;
    this.#page(direction);
  };

  /**
   * Folds a wheel event into the current gesture, or starts a new one.
   *
   * Wheel events have no begin/end, so gestures are inferred from the stream. A pause
   * or a change of direction plainly starts a new one. A trackpad also keeps sending
   * events for a second or more after the fingers lift — momentum, which only ever
   * shrinks — so travel that grows again once the gesture has tailed off is the next
   * swipe, not the last one still coasting. Without that, every swipe made before the
   * previous tail died out would be swallowed.
   *
   * Single events are too noisy to judge that by: a trackpad reports travel in uneven
   * bursts, and the browser merges two inputs into one bigger delta whenever a frame
   * is dropped. The gesture is read through a running average instead, and only counts
   * as a new swipe once that average has clearly decayed and then doubles off its low —
   * momentum can't do that. Each gesture pages at most once.
   *
   * @param {number} direction - 1 when revealing the footer, -1 when closing it.
   * @param {number} magnitude - The travel of the event, in pixels.
   */
  #trackWheel(direction, magnitude) {
    const now = performance.now();
    const paused = now - this.#wheelLast > WHEEL_GESTURE_GAP;
    const reversed = direction !== this.#wheelDirection;

    // A tiny event against the grain is noise, not a reversal.
    if (reversed && !paused && magnitude < WHEEL_NOISE_FLOOR) return;

    let average =
      this.#wheelAverage + (magnitude - this.#wheelAverage) * WHEEL_SMOOTHING;

    const fresh =
      paused ||
      reversed ||
      (this.#wheelCoasting &&
        average >= WHEEL_NOISE_FLOOR &&
        average > this.#wheelTrough * 2);

    if (fresh) {
      average = magnitude;

      this.#wheelDirection = direction;
      this.#wheelDelta = 0;
      this.#wheelPeak = 0;
      this.#wheelTrough = 0;
      this.#wheelCoasting = false;
      this.#wheelSpent = false;
    }

    this.#wheelLast = now;
    this.#wheelAverage = average;
    this.#wheelDelta += magnitude;

    if (average > this.#wheelPeak) {
      this.#wheelPeak = average;
    } else if (!this.#wheelCoasting) {
      // Momentum has clearly set in once the travel has shrunk to half the peak.
      if (average < this.#wheelPeak / 2) {
        this.#wheelCoasting = true;
        this.#wheelTrough = average;
      }
    } else if (average < this.#wheelTrough) {
      this.#wheelTrough = average;
    }
  }

  /** @param {TouchEvent} event - The touch event. */
  #handleTouchStart = (event) => {
    this.#touchStart = event.touches[0]?.clientY ?? null;
    this.#touchSpent = false;
  };

  /** @param {TouchEvent} event - The touch event. */
  #handleTouchMove = (event) => {
    const y = event.touches[0]?.clientY;

    if (y == null || event.touches.length > 1 || !this.#paging()) return;

    // One transition per swipe: once the gesture has paged, keep swallowing it so the
    // finger trailing on doesn't scroll the page underneath the animation.
    if (this.#touchSpent) {
      event.preventDefault();
      return;
    }

    if (this.#touchStart == null) return;

    const travelled = this.#touchStart - y;

    if (travelled === 0) return;

    const direction = travelled > 0 ? 1 : -1;

    // At either end the gesture is left alone so the visitor can carry on up the page.
    if (!this.#canPage(direction)) {
      this.#touchStart = null;
      return;
    }

    // iOS only honors preventDefault before native scrolling has begun, so the gesture
    // has to be claimed on its very first movement — waiting for the swipe threshold
    // would be too late and the reveal would free-scroll.
    event.preventDefault();

    if (Math.abs(travelled) < 24) return;

    this.#touchStart = null;
    this.#touchSpent = true;
    this.#page(direction);
  };

  /** @param {KeyboardEvent} event - The keyboard event. */
  #handleKeydown = (event) => {
    if (!this.#paging()) return;

    const down =
      event.key === "ArrowDown" ||
      event.key === "PageDown" ||
      event.key === " ";
    const up = event.key === "ArrowUp" || event.key === "PageUp";

    if (!down && !up) return;

    const { target } = event;

    if (
      target instanceof HTMLElement &&
      target.closest("input, textarea, select, [contenteditable]")
    )
      return;

    const direction = down ? 1 : -1;

    if (!this.#canPage(direction)) return;

    event.preventDefault();
    this.#page(direction);
  };
}

if (!customElements.get("footer-reveal")) {
  customElements.define("footer-reveal", FooterReveal);
}
